import React, { FC, useEffect, useMemo } from 'react';

import { Box, Grid } from '@mui/material';
import { useFormik } from 'formik';

import {
  cancelContractRequest,
  pharmacistActions,
  successSelector,
  WorkshiftStatus,
} from '@pharmaplan/common';
import { mergeContractAttributes } from '@pharmaplan/common/helpers/utils';

import RequestContractDetails from '../RequestContractModal/RequestContractDetails';
import useViewEditContract from '../../../../hooks/workshift/useViewEditContract';
import { useAppDispatch } from '../../../../hooks/useAppDispatch';
import { resetDialog } from '../../../../reducers/dialogReducer';
import genericClasses from '../../../../theme/GenericClasses';
import { CancelContractType, Constants } from '../../../../helpers/Constants';
import CustomButton from '../../../common/CustomButton';
import strings from '../../../../localization';

import CancelContractTimes from './CancelContractTimes';
import styles from './styles';
import useCancelContractRequestModal from '../../../../hooks/CancelContractRequestModal/useCancelContractRequestModal';
import { useAppSelector } from '../../../../hooks/useAppSelector';

interface ICancelContractRequestModal {
  getEvent?: () => void;
  longTermContractId?: string;
  eventType: CancelContractType;
  successAction: string;
}

const CancelContractRequestModal: FC<ICancelContractRequestModal> = ({
  longTermContractId,
  successAction,
  eventType,
  getEvent,
}) => {
  const dispatch = useAppDispatch();
  const success = useAppSelector((state) =>
    successSelector([pharmacistActions.cancelContractRequest], state));

  const { intervalsInitial } = useViewEditContract();
  const events = useCancelContractRequestModal(eventType, longTermContractId);

  const { shifts, longTermShiftContract, counterShiftContract } = events ?? {};

  const {
    allowanceNotes,
    weekDayPaidHourlyRate,
    weekendPaidHourlyRate,
    longTermShiftContractId,
  } = longTermShiftContract ?? {};

  const {
    weekDayPaidHourlyRate: counterWeekDayPaidHourlyRate,
    weekendPaidHourlyRate: counterWeekendPaidHourlyRate,
    allowanceNotes: counterAllowanceNotes,
    shiftContractAttributes,
  } = counterShiftContract ?? {};

  const counterShifts = useMemo(
    () =>
      shiftContractAttributes?.map((item) =>
        item.startDate?.split('T')[0])
      ?? [],
    [shiftContractAttributes],
  );

  const shiftsMerged = useMemo(
    () => {
      if (counterShiftContract) {
        return mergeContractAttributes(
          shifts ?? [],
          shiftContractAttributes ?? [],
          { status: WorkshiftStatus.Requested },
        );
      }
      return shifts;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [shiftContractAttributes, shifts],
  );

  const handleClose = () => {
    dispatch(resetDialog());
  };

  const handleSubmit = () => {
    dispatch(cancelContractRequest(longTermShiftContractId ?? ''));
  };

  const formik = useFormik({
    initialValues: {
      allowanceNotes,
      proposedAllowance: counterAllowanceNotes,
      workshiftIds: [],
      weekDayPaidHourlyRate:
        counterWeekDayPaidHourlyRate ?? weekDayPaidHourlyRate,
      weekendPaidHourlyRate:
        counterWeekendPaidHourlyRate ?? weekendPaidHourlyRate,
    },
    onSubmit: handleSubmit,
  });

  useEffect(() => {
    if (shiftsMerged) {
      formik.setValues({
        ...formik.values,
        ...intervalsInitial(shiftsMerged),
      });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shiftsMerged]);

  useEffect(() => {
    getEvent?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (success) {
      dispatch(resetDialog());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [success]);

  return (
    <Box sx={styles.container}>
      <Box sx={styles.subContainer}>
        <Grid columnSpacing="15px" container>
          <Grid md={4} xs={12} item>
            <RequestContractDetails
              successAction={successAction}
              isUpdate
              formik={formik}
              events={events}
            />
          </Grid>
          <Grid md={8} xs={12} item>
            <CancelContractTimes
              counterShifts={counterShifts}
              shifts={shifts ?? []}
              formik={formik}
            />
          </Grid>
        </Grid>
      </Box>
      <Box sx={[styles.buttonContainer, genericClasses.flex]}>
        <CustomButton
          label={strings.cancel}
          onClick={handleClose}
          variant={Constants.variant.outlined}
        />
        <CustomButton
          customClass={styles.saveButton}
          onClick={formik.handleSubmit}
          label={strings.cancelRequest}
        />
      </Box>
    </Box>
  );
};

export default CancelContractRequestModal;
